import {ActivatedRouteSnapshot, Resolve, RouterStateSnapshot} from '@angular/router';

import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    PaymentProcessesEnum,
    ReportsService,
    AuthApiService,
    PaymentReportModel,
    PaymentReportInterface,
    PaymentFilterModel
} from '@spa/api';

import {FilterControl, Helper} from '../../../shared/classes';
import {ReportTableConfigInterface} from '../../../shared/components';

import {PaymentsReportResolverDataInterface} from '../interfaces';
import {PermissionsEnum} from '../../../shared/enums';
import {ComponentStorageKeyProvider} from 'appShared/providers';
import {CacheCollectionService} from 'appShared/services';

export abstract class PaymentsReportResolver
    implements Resolve<ApiResponse<ApiError[] | PaymentsReportResolverDataInterface>> {
    protected componentStorageKeyProvider: ComponentStorageKeyProvider;
    protected constructor(protected reportsService: ReportsService,
                          protected authApiService: AuthApiService,
                          protected cacheCollectionService: CacheCollectionService,
                          protected storageId: string) {
        this.componentStorageKeyProvider = new ComponentStorageKeyProvider(storageId);
    }

    private mapData(data: PaymentReportInterface[]): PaymentReportModel[] {
        return data.map((report: PaymentReportInterface) => {
            const model = new PaymentReportModel(report);

            model.createDate = Helper.formatDate(model.createDate);
            model.settledDate = Helper.formatDate(model.settledDate);

            return model;
        });
    }

    protected abstract getDefaultFilterModel(): PaymentFilterModel;

    protected abstract getFilterControls(): FilterControl[];

    protected abstract getDataTableConfig(): ReportTableConfigInterface;

    resolve(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<ApiResponse<ApiError[] | PaymentsReportResolverDataInterface>> {

        const viewPermissionsExpectCreate = [
            PermissionsEnum.CrmPaymentsViewDetails,
            PermissionsEnum.CrmPaymentsUpdateDetails
        ];

        const authUserPermissions = !this.authApiService.getAuthenticatedUser() ? [] :
            this.authApiService.getAuthenticatedUser().permissions;

        if (!Helper.getUnionStrategyPermissionsCheck(viewPermissionsExpectCreate, authUserPermissions)) {
            const allowedProcesses = [];

            if (authUserPermissions.indexOf(PermissionsEnum.CrmPaymentsCreateNewWithdrawal) >= 0) {
                allowedProcesses.push(PaymentProcessesEnum.Withdrawal);
            }

            if (authUserPermissions.indexOf(PermissionsEnum.CrmPaymentsCreateNewDeposit) >= 0) {
                allowedProcesses.push(PaymentProcessesEnum.Funding);
            }

            if (authUserPermissions.indexOf(PermissionsEnum.CrmPaymentsCreateNewTransfer) >= 0) {
                allowedProcesses.push(PaymentProcessesEnum.Transfer);
            }
        }
        const filterControls = this.getFilterControls();
        const filterCache = this.cacheCollectionService.getItem(this.componentStorageKeyProvider.getFilterModelKey())?.data;
        const filter = filterCache ?? this.getDefaultFilterModel();
        // TODO:: Add types to filter when it is possible and fix filter control

        return this.reportsService.getPaymentsReport(filter)
            .pipe(
                map((response: ApiResponse<ApiError[] | PaymentReportInterface[]>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponse(response);
                    }

                    const data = response.data;

                    let modelsCollection = [];

                    if (data !== null) {
                        modelsCollection = this.mapData(data as PaymentReportInterface[]);
                    }

                    return new ApiResponse<PaymentsReportResolverDataInterface>(HttpStatusCodesEnum.Ok, {
                        filterControls,
                        dataTableConfig: this.getDataTableConfig(),
                        modelsCollection,
                        filterModel: filter
                    });
                })
            );
    }
}
